import { useState, useEffect, useRef } from "react";
import { MinusIcon, PlusIcon } from "lucide-react";
import { Input } from ".";
import { Button } from "../button";

export function NumberCounterInput({
  value,
  onChange,
  step = 1,
  precision = 2,
  max,
  min,
  displayFormatter,
  disabled,
}: {
  value: number | string;
  onChange?: (value: number) => void;
  step?: number | ((val: number, dir?: "inc" | "dec") => number);
  unit?: string;
  precision?: number;
  min?: number;
  max?: number;
  displayFormatter?: (value: number | string) => string;
  disabled?: boolean;
}) {
  const [inputValue, setInputValue] = useState<string>(
    Number.isNaN(Number(value)) ? "" : Number(value).toString()
  );
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync internal state when external value changes
  useEffect(() => {
    if (isEditing) return;
    setInputValue(
      Number.isNaN(Number(value).toFixed(precision))
        ? ""
        : Number(value).toFixed(precision)
    );
  }, [value, precision, isEditing]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const clampValue = (val: number): number => {
    let result = val;
    if (typeof min !== "undefined") {
      result = Math.max(result, min);
    }
    if (typeof max !== "undefined") {
      result = Math.min(result, max);
    }
    return result;
  };

  const increment = () => {
    const newValue = +parseFloat(
      (
        Number(value) +
        (typeof step === "function" ? step(Number(value), "inc") : step)
      ).toString()
    ).toFixed(precision);
    onChange?.(clampValue(newValue));
  };

  const decrement = () => {
    const newValue = +parseFloat(
      (
        Number(value) -
        (typeof step === "function" ? step(Number(value), "dec") : step)
      ).toString()
    ).toFixed(precision);
    onChange?.(clampValue(newValue));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Only trigger onChange if the value is a valid non-empty number
    if (newValue !== "" && !Number.isNaN(Number(newValue))) {
      const numValue = +parseFloat(Number(newValue).toFixed(precision));
      onChange?.(clampValue(numValue));
    }
  };

  const handleBlur = () => {
    // On blur, if empty or invalid, reset to current value
    if (inputValue === "" || Number.isNaN(Number(inputValue))) {
      setInputValue(value.toString());
    }
    setIsEditing(false);
  };

  const handleDisplayClick = () => {
    setIsEditing(!disabled && true);
  };

  return (
    <div
      className={`flex flex-row rounded-md border ${isEditing ? "border-input" : "border-transparent"}`}
    >
      <Button
        onClick={decrement}
        variant="outline"
        size="icon"
        type="button"
        disabled={disabled}
        className={`flex-shrink-0 shadow-none ${isEditing ? "rounded-r-none rounded-l-md border-0" : "rounded-md"}`}
      >
        <MinusIcon />
      </Button>
      <div
        className={`flex flex-1 flex-row items-center justify-center min-w-[80px] h-9 cursor-text rounded-sm ${!isEditing ? "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0" : ""}`}
        onClick={handleDisplayClick}
        onKeyDown={(e) => {
          if (!isEditing && !disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setIsEditing(true);
          }
        }}
        tabIndex={disabled ? -1 : 0}
        role="spinbutton"
        aria-valuenow={Number.isNaN(Number(value)) ? undefined : Number(value)}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-disabled={disabled}
      >
        {isEditing ? (
          <>
            <Input
              ref={inputRef}
              value={inputValue}
              type="number"
              step="any"
              min={min}
              max={max}
              onChange={handleInputChange}
              onBlur={handleBlur}
              className="border-none p-0 pl-1 h-auto flex-1 text-center outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </>
        ) : (
          <span className="text-sm px-2">
            {displayFormatter ? displayFormatter(value) : value}
          </span>
        )}
      </div>
      <Button
        className={`flex-shrink-0 shadow-none ${isEditing ? "rounded-l-none rounded-r-md border-0" : "rounded-md"}`}
        onClick={increment}
        variant="outline"
        type="button"
        size="icon"
        disabled={disabled}
      >
        <PlusIcon />
      </Button>
    </div>
  );
}
